import { FC, ReactNode, SVGProps } from 'react';
import { AuthMethod as AuthMethodBase, Config, Theme } from '@coinbase/cdp-react';
import { CheckboxCellProps } from '@cbhq/cds-web/controls/CheckboxCell';

import { accentColors } from './data/accentColors';
import { borderRadiusMap } from './data/borderRadius';
import { allFonts } from './data/fonts';

export const authMethodDataKey = Symbol('authMethod');

export function isNonEmptyArray<T>(arr: T[]): arr is [T, ...T[]] {
  return arr.length > 0;
}

export type AuthMethodData = { [authMethodDataKey]: true; authMethodId?: AuthMethod };

export type AuthMethodOption = {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  paddingY?: CheckboxCellProps<AuthMethod>['paddingY'];
  readOnly?: boolean;
  registerRef?: (id: AuthMethod, ref: HTMLDivElement | null) => void;
};

export type AuthMethodConfig = {
  value: AuthMethod;
  label: string;
  icon: FC<SVGProps<SVGElement>>;
  disabled?: boolean;
};

export type AccountCreationType = 'evm-eoa' | 'evm-smart' | 'solana';

export function isAccountCreationType(value: string): value is AccountCreationType {
  return ['evm-eoa', 'evm-smart', 'solana'].includes(value);
}

export type AuthMethod = AuthMethodBase;

export type BorderRadius = keyof typeof borderRadiusMap;

export function isBorderRadius(value: string): value is BorderRadius {
  return !!borderRadiusMap[value as BorderRadius];
}

export type FontFamily = keyof typeof allFonts;

export function isFontFamily(value: string): value is FontFamily {
  return !!allFonts[value as FontFamily];
}

export type ThemeMode = 'light' | 'dark';

export function isThemeMode(value: string): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

export type AccentColorName = (typeof accentColors)[number];

export function isAccentColorName(color: string): color is AccentColorName {
  return accentColors.includes(color as AccentColorName);
}

export type HexColor = `#${string}`;

export function isHexColor(color: string): color is HexColor {
  return /^#([0-9a-fA-F]{6})$/.test(color);
}

export type AccentColor = AccentColorName | HexColor;

export function isAccentColor(color: string): color is AccentColor {
  return isAccentColorName(color) || isHexColor(color);
}

export type ConfigProps = {
  config: Partial<
    Pick<
      Config,
      'appName' | 'appLogoUrl' | 'authMethods' | 'showCoinbaseFooter' | 'ethereum' | 'solana'
    >
  >;
  theme: Partial<Theme>;
};

export type ConfigState = {
  accentColor: AccentColor;
  borderRadius?: BorderRadius;
  config: ConfigProps['config'];
  customColor: HexColor | '' | undefined;
  fontFamily: FontFamily;
  themeMode: ThemeMode;
};

export type ConfigFormHandlers = {
  setAccentColor: (accentColor: AccentColor, customColor?: HexColor) => void;
  setAccountCreationType: (accountCreationType: AccountCreationType) => void;
  setAppLogoUrl: (appLogoUrl: string) => void;
  setAppName: (appName: string) => void;
  setBorderRadius: (borderRadius: BorderRadius) => void;
  setAuthMethods: (authMethods: AuthMethod[]) => void;
  setFontFamily: (fontFamily: FontFamily) => void;
  setThemeMode: (themeMode: ThemeMode) => void;
  toggleAuthMethod: (authMethod: AuthMethod, checked: boolean) => void;
  toggleCoinbaseFooter: () => void;
};

export type ThemeConfig = Omit<ConfigState, 'config'>;

export type AppearanceAppConfig = Pick<
  ConfigState['config'],
  'appLogoUrl' | 'appName' | 'showCoinbaseFooter'
>;
